/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useEffect, useState } from "react";

const eventName = "call-fullscreen-change";
const nativeElement = (): Element | null =>
  document.fullscreenElement || document.webkitFullscreenElement || null;
const active = (): boolean =>
  !!nativeElement() || document.body.dataset.callFullscreen === "true";
const clearLegacyFallback = (): void => {
  delete document.body.dataset.callFullscreen;
  window.dispatchEvent(new Event(eventName));
};
export function canEnterCallFullscreen(): boolean {
  return (
    (typeof document.body.requestFullscreen === "function" &&
      document.fullscreenEnabled !== false) ||
    typeof document.body.webkitRequestFullscreen === "function"
  );
}
export async function exitCallFullscreen(): Promise<void> {
  clearLegacyFallback();
  if (!nativeElement()) return;
  try {
    if (document.exitFullscreen) await document.exitFullscreen();
    else document.webkitExitFullscreen?.();
  } catch {
    /* Browser may already have left fullscreen. */
  }
}
export async function toggleCallFullscreen(): Promise<void> {
  if (active()) {
    await exitCallFullscreen();
    return;
  }
  try {
    if (document.body.requestFullscreen)
      await document.body.requestFullscreen();
    else if (document.body.webkitRequestFullscreen)
      document.body.webkitRequestFullscreen();
  } catch {
    // Unsupported/refused native fullscreen leaves the ordinary call intact.
  }
}
export function useCallFullscreen(): boolean {
  const [value, setValue] = useState(active);
  useEffect(() => {
    let hadNative = !!nativeElement();
    const update = (): void => {
      const native = !!nativeElement();
      if (hadNative && !native) delete document.body.dataset.callFullscreen;
      hadNative = native;
      setValue(active());
    };
    const escape = (event: KeyboardEvent): void => {
      if (
        event.key === "Escape" &&
        document.body.dataset.callFullscreen === "true"
      )
        clearLegacyFallback();
    };
    document.addEventListener("fullscreenchange", update);
    document.addEventListener("webkitfullscreenchange", update);
    window.addEventListener(eventName, update);
    window.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("fullscreenchange", update);
      document.removeEventListener("webkitfullscreenchange", update);
      window.removeEventListener(eventName, update);
      window.removeEventListener("keydown", escape);
    };
  }, []);
  return value;
}
